import * as React from "react";
import { MobXProviderContext, observer } from "mobx-react";
import { Box, Button, Checkbox, Grid, Hidden } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import * as styles from "../styles.scss";
import cx from "classnames";
import { SvgIcon } from "../../../../../../components";
import { tl } from "../../../../../../utils";

import { IFile } from "../../../types";

import { Audio } from "./Audio";

interface IProps {
  data: IFile;
  grouped?: boolean;
  canDelete?: boolean;
}

export const FilecardAudio = observer((props: IProps) => {
  const { fsStore } = React.useContext(MobXProviderContext);
  const theme = useTheme();

  const fileAction = (function(category) {
    switch (category) {
      case "video":
      case "audio":
        return tl("Воспроизвести");
      case "image":
        return tl("Посмотреть");
      default:
        return tl("Скачать");
    }
  })(props.data.category);

  const fileInfo = (
    <Box
      py={1.5}
      pr={2}
      pl={props.grouped && !fsStore.isFilesCheckAvailable ? 4 : 2}
    >
      {props.data.category === "audio" ? (
        <Audio src={props.data.srcUrl} {...props.data} />
      ) : (
        <Grid container spacing={1}>
          <Grid item>
            <Box className={styles.thumb}>
              {props.data.category === "image" ? (
                <img
                  src={props.data.icon || props.data.srcUrl}
                  className={styles.thumbImage}
                />
              ) : props.data.category === "video" && props.data.icon ? (
                <>
                  <SvgIcon type={`fs-filetype-${props.data.category}`} />
                  <img
                    src={props.data.icon || props.data.srcUrl}
                    className={styles.thumbImage}
                  />
                </>
              ) : (
                <SvgIcon type={`fs-filetype-${props.data.category}`} />
              )}
            </Box>
          </Grid>
          <Grid item xs>
            <Grid container title={`${props.data.name}${props.data.extension}`}>
              <Box textOverflow="ellipsis" clone>
                <Grid item className={styles.filenameName}>
                  {props.data.name}
                </Grid>
              </Box>
              <Grid item className={styles.filenameExtension}>
                {props.data.extension}
              </Grid>
            </Grid>
            <Box color="text.secondary">
              {props.data.protected ? (
                <SvgIcon
                  type="fs-list-protector"
                  title={tl(
                    "Ваш файл защищен, чтобы выключить защиту зайдите в его настройки в пункт «Защита файла»."
                  )}
                />
              ) : null}
              {props.data.size}{" "}
              {props.data.date ? <Hidden smUp>{props.data.date}</Hidden> : null}
            </Box>
          </Grid>
          <Hidden only="xs">
            <Grid item style={{ alignSelf: "center" }}>
              <span className="filestorage-list__item-date__option">
                {fileAction}
              </span>
              <span>{props.data.date}</span>
            </Grid>
          </Hidden>
        </Grid>
      )}
    </Box>
  );

  return (
    <Grid item>
      <Box className={styles.file}>
        <Grid container wrap="nowrap" spacing={0}>
          {fsStore.isFilesCheckAvailable || props.canDelete ? (
            <>
              <Grid item xs>
                <Box bgcolor={theme.palette.grey.A100} clone>
                  <Grid
                    container
                    wrap="nowrap"
                    className={`react-hideBgOnHover ${styles.fileCardWrap}`}
                  >
                    <Box
                      clone
                      bgcolor={theme.palette.grey.A100}
                      className="react-hideBgOnHover"
                    >
                      <Grid
                        item
                        component="label"
                        htmlFor={`filestorage-fileCheck-${props.data.id}`}
                        className={styles.fileCheck}
                      >
                        <Checkbox
                          color="primary"
                          size="small"
                          id={`filestorage-fileCheck-${props.data.id}`}
                          checked={fsStore.isFileChecked(props.data)}
                          onChange={() => fsStore.onFileCheck(props.data)}
                        />
                      </Grid>
                    </Box>
                    <Box bgcolor={theme.palette.grey.A100} clone>
                      <Grid
                        item
                        xs
                        className={`react-hideBgOnHover ${styles.fileCard}`}
                      >
                        {fileInfo}
                      </Grid>
                    </Box>
                  </Grid>
                </Box>
              </Grid>

              {!fsStore.getCheckedFilesCount ? (
                <Hidden only="xs">
                  <Box p={1} bgcolor={theme.palette.grey.A100} clone>
                    <Grid
                      item
                      onClick={() => {
                        fsStore.onAttachAll([props.data]);
                      }}
                      className={`react-hideBgOnHover ${styles.fileActionButton}`}
                    >
                      <Button
                        type="button"
                        variant="contained"
                        size="small"
                        color="primary"
                      >
                        {tl("Прикрепить")}
                      </Button>
                    </Grid>
                  </Box>
                </Hidden>
              ) : null}

              {fsStore.canDownload ? (
                <Hidden only="xs">
                  <Box bgcolor={theme.palette.grey.A100} clone>
                    <Grid
                      item
                      component="a"
                      href={props.data.downloadUrl}
                      target="_blank"
                      title={
                        props.data.category === "video"
                          ? tl(
                              "Видео находится на обработке, просмотреть его в данный момент невозможно. Но вы можете скачать исходную версию файла."
                            )
                          : tl("Скачать")
                      }
                      className={`react-hideBgOnHover ${styles.fileControl}`}
                    >
                      <SvgIcon type="fs-list-download" />
                    </Grid>
                  </Box>
                </Hidden>
              ) : null}
            </>
          ) : ["video", "image", "audio"].indexOf(props.data.category) == -1 &&
            fsStore.canDownload ? (
            <Box bgcolor={theme.palette.grey.A100} clone>
              <Grid
                item
                xs
                className={`react-hideBgOnHover ${styles.fileCard}`}
              >
                <Grid container>
                  <Grid item xs>
                    {fileInfo}
                  </Grid>
                  <Hidden only="xs">
                    <Grid
                      item
                      component="a"
                      href={props.data.downloadUrl}
                      target="_blank"
                      title={tl("Скачать")}
                      className={styles.fileControl}
                    >
                      <SvgIcon type="fs-list-download" />
                    </Grid>
                  </Hidden>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <>
              <Box bgcolor={theme.palette.grey.A100} clone>
                <Grid
                  item
                  xs
                  className={`react-hideBgOnHover ${styles.fileCard}`}
                >
                  {fileInfo}
                </Grid>
              </Box>

              {fsStore.canDownload ? (
                <Hidden only="xs">
                  <Box bgcolor={theme.palette.grey.A100} clone>
                    <Grid
                      item
                      component="a"
                      href={props.data.downloadUrl}
                      target="_blank"
                      title={tl("Скачать")}
                      className={`react-hideBgOnHover ${styles.fileControl}`}
                    >
                      <SvgIcon type="fs-list-download" />
                    </Grid>
                  </Box>
                </Hidden>
              ) : null}
            </>
          )}

          {fsStore.canEdit ? (
            <Box bgcolor={theme.palette.grey.A100} clone>
              <Grid
                item
                component="a"
                href={`/panel/files/edit?course_id=${
                  fsStore.course_id ? fsStore.course_id : props.data.course
                }&id=${props.data.id}`}
                target="_blank"
                title={tl("Настройки файла")}
                className={`react-hideBgOnHover ${styles.fileControl}`}
              >
                <SvgIcon type="fs-list-settings" />
              </Grid>
            </Box>
          ) : null}
        </Grid>
      </Box>
    </Grid>
  );
});
